/**
 * Turns Counsel's legal/*.md into the text shown on the public policy pages.
 * Drafting notes and "do not publish" instructions are removed. The effective
 * date is POLICY_EFFECTIVE_DATE. Last Updated is POLICY_LAST_UPDATED.
 * The trial file is an implementation kit; only its standalone /trial-terms
 * section is published.
 */

const INSTRUCTION = /DO NOT PUBLISH|PRE-PUBLICATION|DO NOT LIST|IF ADDED|CURRENT STATUS|UPDATE AFTER|INDEPENDENT AUDITOR|PRIVACY REQUEST WEB FORM|DRAFTING NOTE|NAME OR TITLE/i;

// property-photos and task-photos are private. The app stores an object address and reads with a signed URL.
export const PUBLIC_PHOTO_SENTENCE = 'Property photos and task photos are stored privately. Pacarion shows them with a short-lived link after it checks that you are allowed to see that home or task.';

export function publishableLegalMarkdown(markdown, effectiveDate, lastUpdated) {
  const date = String(effectiveDate || '').trim();
  const updated = String(lastUpdated || '').trim();
  let text = String(markdown || '').replace(/\r\n/g, '\n');
  text = text.replace(/\[[^\]]*PRE-PUBLICATION CHECK[^\]]*\]/gi, PUBLIC_PHOTO_SENTENCE);
  text = text.replace(/\n---\s*\n+## Appendix:[\s\S]*$/i, '\n');
  text = text
    .split('\n')
    .filter((line) => {
      const trimmed = line.trim();
      if (/^\s*>\s*DRAFT\b/i.test(trimmed)) return false;
      if (/^\s*(?:>\s*)?\*{0,2}Status:\*{0,2}\s*DRAFT\b/i.test(trimmed)) return false;
      if (/do not publish/i.test(trimmed)) return false;
      return true;
    })
    .join('\n');
  text = text.replace(/\n---\s*$/g, '');
  text = text.replace(/\[EFFECTIVE DATE[^\]]*\]/gi, date);
  if (updated) text = text.replace(/\[LAST UPDATED[^\]]*\]/gi, updated);
  text = text.replace(/, phone:\s*\[PHONE\]/gi, '');
  text = text.replace(/,?\s*or use\s*\[PRIVACY REQUEST WEB FORM URL\]/gi, '');
  text = text.replace(/\[[^\]\n]*\]/g, (match) => replaceBracket(match));
  text = text.replace(/[ \t]{2,}/g, ' ');
  text = text.replace(/[ \t]+([.,;:])/g, '$1');
  text = text.replace(/\*\*:/g, ':**');
  text = text.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  return `${text}\n`;
}

export function standaloneTrialTerms(markdown) {
  const text = String(markdown || '').replace(/\r\n/g, '\n');
  const start = text.search(/^## H\. Standalone[^\n]*$/m);
  if (start === -1) return text;
  const rest = text.slice(start);
  const next = rest.slice(1).search(/\n## [A-Z]\. /);
  const section = next === -1 ? rest : rest.slice(0, next + 1);
  const body = section
    .split('\n')
    .filter((line) => !/^## H\./.test(line.trim()))
    .filter((line) => !/automatic_tax|Stripe Tax|Stripe configuration|Do not set `automatic_tax`|Counsel's text/i.test(line))
    .map((line) => line.replace(/^\s*>\s?/, ''))
    .join('\n')
    .replace(
      // Section H already carries Last Updated. Consume that line so the page shows it once.
      /\*\*Pacarion LLC Free Trial & Automatic Renewal Terms\*\*\s*\(Effective \[DATE\]\)(?:\s*\*\*Last Updated:\*\*\s*\[LAST UPDATED\])?/,
      '# Pacarion LLC Free Trial & Automatic Renewal Terms\n\n**Effective Date:** [EFFECTIVE DATE]\n**Last Updated:** [LAST UPDATED]'
    )
    .replace(/\[Refund & Cancellation Policy\](?!\()/g, '[Refund & Cancellation Policy](/refunds)');
  return body;
}

function replaceBracket(match) {
  const inner = match.slice(1, -1).trim();
  if (/^We do not currently respond to DNT signals\.?$/i.test(inner)) {
    return inner.endsWith('.') ? inner : `${inner}.`;
  }
  if (/^Supabase:/i.test(inner)) return `(${inner})`;
  if (/^Pacarion does not currently send SMS/i.test(inner)) {
    return 'Pacarion does not currently send SMS.';
  }
  if (/^X months\b/i.test(inner)) return 'Until you delete them or your account is deleted';
  if (/^e\.g\./i.test(inner)) return inner;
  if (/^If annual plans are ever offered/i.test(inner)) return '';
  if (/NAME OR TITLE/i.test(inner)) return 'Copyright Agent';
  if (/^PHONE$/i.test(inner)) return '';
  if (INSTRUCTION.test(inner)) return '';
  return match;
}

export function parseInline(text) {
  const nodes = [];
  let buffer = '';
  let index = 0;
  const flush = () => {
    if (buffer) nodes.push(buffer);
    buffer = '';
  };
  while (index < text.length) {
    if (text.startsWith('**', index)) {
      const end = text.indexOf('**', index + 2);
      if (end !== -1) {
        flush();
        nodes.push({ type: 'bold', children: parseInline(text.slice(index + 2, end)) });
        index = end + 2;
        continue;
      }
    }
    if (text[index] === '*' && text[index + 1] !== '*') {
      const end = text.indexOf('*', index + 1);
      if (end !== -1 && text[end + 1] !== '*') {
        flush();
        nodes.push({ type: 'italic', children: parseInline(text.slice(index + 1, end)) });
        index = end + 1;
        continue;
      }
    }
    if (text[index] === '`') {
      const end = text.indexOf('`', index + 1);
      if (end !== -1) {
        flush();
        nodes.push({ type: 'code', text: text.slice(index + 1, end) });
        index = end + 1;
        continue;
      }
    }
    if (text[index] === '[') {
      const labelEnd = text.indexOf(']', index + 1);
      if (labelEnd !== -1 && text[labelEnd + 1] === '(') {
        const hrefEnd = text.indexOf(')', labelEnd + 2);
        if (hrefEnd !== -1) {
          flush();
          nodes.push({
            type: 'link',
            href: text.slice(labelEnd + 2, hrefEnd),
            children: parseInline(text.slice(index + 1, labelEnd)),
          });
          index = hrefEnd + 1;
          continue;
        }
      }
    }
    buffer += text[index];
    index += 1;
  }
  flush();
  return nodes;
}

function isTableRow(line) {
  return /^\s*\|/.test(line);
}

function isDividerRow(line) {
  return /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);
}

function splitRow(line) {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map((cell) => cell.trim());
}

function listMatch(line) {
  const unordered = /^\s*[-*]\s+(.+)$/.exec(line);
  if (unordered) return { ordered: false, text: unordered[1] };
  const ordered = /^\s*\d+\.\s+(.+)$/.exec(line);
  if (ordered) return { ordered: true, text: ordered[1] };
  return null;
}

export function parseLegalMarkdown(markdown) {
  const lines = String(markdown || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let index = 0;

  const pushParagraph = (paragraphLines) => {
    const text = paragraphLines.join(' ').trim();
    if (text) blocks.push({ type: 'p', children: parseInline(text) });
  };

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }
    if (/^---\s*$/.test(line.trim())) {
      blocks.push({ type: 'hr' });
      index += 1;
      continue;
    }
    const heading = /^(#{1,3})\s+(.+)$/.exec(line);
    if (heading) {
      let text = heading[2].trim();
      let id;
      const anchor = /\s*\{#([A-Za-z][\w-]*)\}\s*$/.exec(text);
      if (anchor) {
        id = anchor[1];
        text = text.slice(0, anchor.index).trim();
      }
      blocks.push({
        type: `h${heading[1].length}`,
        id,
        children: parseInline(text),
      });
      index += 1;
      continue;
    }
    if (isTableRow(line)) {
      const rows = [];
      while (index < lines.length && isTableRow(lines[index])) {
        if (!isDividerRow(lines[index])) rows.push(splitRow(lines[index]).map(parseInline));
        index += 1;
      }
      blocks.push({
        type: 'table',
        headers: rows[0] || [],
        rows: rows.slice(1),
      });
      continue;
    }
    const item = listMatch(line);
    if (item) {
      const ordered = item.ordered;
      const items = [];
      while (index < lines.length) {
        const next = listMatch(lines[index]);
        if (!next || next.ordered !== ordered) break;
        items.push(parseInline(next.text));
        index += 1;
      }
      blocks.push({ type: ordered ? 'ol' : 'ul', items });
      continue;
    }
    if (/^\s*>/.test(line)) {
      const quoted = [];
      while (index < lines.length && /^\s*>/.test(lines[index])) {
        quoted.push(lines[index].replace(/^\s*>\s?/, ''));
        index += 1;
      }
      blocks.push({ type: 'quote', children: parseInline(quoted.join(' ')) });
      continue;
    }
    const paragraph = [];
    while (
      index < lines.length
      && lines[index].trim()
      && !/^(#{1,3})\s+/.test(lines[index])
      && !isTableRow(lines[index])
      && !listMatch(lines[index])
      && !/^\s*>/.test(lines[index])
      && !/^---\s*$/.test(lines[index].trim())
    ) {
      paragraph.push(lines[index].trim());
      index += 1;
    }
    pushParagraph(paragraph);
  }
  return blocks;
}

